import type { ComboboxOptionsProps } from '@headlessui/react';
import type {
  ChangeEvent,
  Dispatch,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
  SetStateAction,
} from 'react';
import type { WebsocketAwarenessState } from '../../hooks/useWebsocket/websocket';
import type { HelpErrorTextsProps } from '../HelpErrorTextsTemplate/HelpErrorTextsTemplate';
import type { DropdownOption } from './DropdownOption/DropdownOption';
import type { DropdownRemoveOptionFn } from './SelectedOptions/SelectedOptions';

import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  Transition,
} from '@headlessui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { EMPTY_ARRAY } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import Loader from '../../components/Loader/Loader';

import useDebounceCallback from '../../hooks/useDebounceCallback';

import {
  ArrowFilledDownIcon,
  CaretDownIcon,
  CloseIcon,
} from '../../images/shapes';

import FieldEditorsIndicator from '../FieldEditorsIndicator/FieldEditorsIndicator';
import HelpErrorTextsTemplate from '../HelpErrorTextsTemplate/HelpErrorTextsTemplate';
import RequiredTemplate from '../RequiredTemplate/RequiredTemplate';
import DropdownOptions from './DropdownOptions/DropdownOptions';
import SelectedOptions from './SelectedOptions/SelectedOptions';

const checkIfIncludes = (string: string, query: string) => {
  return string
    .toLowerCase()
    .replace(/\s+/g, '')
    .includes(query.toLowerCase().replace(/\s+/g, ''));
};

export type DropdownChangeFn = (
  event: {
    target: { value: any; name: string };
    option?: DropdownOption;
  },
  value: any | any[],
) => any | void;

export type DropdownFilterCallback = (
  query: string,
  options: DropdownOption[],
  setIsLoading: Dispatch<SetStateAction<boolean>>,
  currentValue: string,
) => Promise<DropdownOption[] | void>;

export interface DropdownProps extends HelpErrorTextsProps {
  /**
   * Theme
   */
  theme?: 'light' | 'dark';
  /**
   * Options to select
   */
  options: DropdownOption[];
  /**
   * If search input should be hidden
   */
  hideSearch?: boolean;
  /**
   * If multiple options can be selected
   */
  multiple?: boolean;
  /**
   * Selected value
   */
  value?: any | any[];
  /**
   * On selected value change
   */
  onChange?: DropdownChangeFn;
  /**
   * On blur callback
   */
  onBlur?: () => void;
  /**
   * On focus callback
   */
  onFocus?: () => void;
  /**
   * Placeholder for no value selected
   */
  placeholder?: string;
  /**
   * Label above dropdown
   */
  label?: ReactNode;
  /**
   * If value is required
   */
  required?: boolean;
  /**
   * If dropdown is disabled
   */
  disabled?: boolean;
  /**
   * If dropdown is readonly (non-interactive but without disabled classes)
   */
  readonly?: boolean;
  /**
   * If nullable value is allowed
   */
  nullable?: boolean;
  /**
   * If filtering by groups is allowed
   */
  filterGroups?: boolean;
  /**
   * Name for dropdown
   */
  name?: string;
  /**
   * Render empty state callback
   */
  renderEmpty?: (query: string) => ReactNode;
  /**
   * Empty state when there is no options
   */
  emptyOptions?: ReactNode;
  /**
   * Filter callback
   */
  filterCallback?: DropdownFilterCallback;
  /**
   * If data are loading
   */
  isDataLoading?: boolean;
  /**
   * Loading icon
   */
  loadingIcon?: ReactNode;
  /**
   * Additional classes for dropdown options
   */
  additionalOptionsClasses?: string;
  /**
   * Dropdown additional classes
   */
  additionalClasses?: string;
  /**
   * Dropdown additional classes
   */
  additionalErrorClasses?: string;
  /**
   * Test id for dropdown
   */
  testId?: string;
  /**
   * Dropdown additional dropdown classes
   */
  additionalDropdownClasses?: string;
  /**
   * Anchor configuration for the options panel.
   */
  anchor?: ComboboxOptionsProps['anchor'];
  /**
   * Dropdown additional container classes
   */
  additionalContainerClasses?: string;
  /**
   * Dropdown additional input classes
   */
  additionalInputClasses?: string;
  /**
   * Dropdown delay update change value on change input
   */
  debounceTime?: number;
  /**
   * Disable red-styled label on unfounded element
   */
  ignoreNotFound?: boolean;
  /**
   * Render add element option when there is no exact match while searching
   */
  renderOnEmptyMatch?: (query: string) => ReactNode;
  /**
   * Extra options for dropdown
   */
  extraOptions?: DropdownOption[];
  /**
   * Placeholder for multiple when more than one selected
   */
  placeholderMultiple?: string;
  /**
   * If dropdown is multiple and should show limited selected options
   */
  maxVisibleValues?: number;
  /**
   * If max visible values is provided and the default text should be overridden
   */
  maxVisibleValuesText?: string;
  /**
   * If filter callback should be called on empty query
   */
  filterCallbackOnEmpty?: boolean;
  /**
   * If virtual scrolling should be used
   */
  isVirtualScrolling?: boolean;
  /**
   * Editors data who are currently editing the field
   */
  editors?: WebsocketAwarenessState[];
  /**
   * Drodpown variant
   */
  variant?: 'default' | 'operator' | 'filter';
  /**
   * Additional help text classes
   */
  additionalHelpTextClasses?: string;
}

const Dropdown = ({
  options,
  hideSearch = false,
  multiple = false,
  value = '',
  onChange,
  onBlur,
  onFocus,
  placeholder = '',
  label = '',
  required,
  disabled = false,
  readonly = false,
  nullable = false,
  filterGroups = false,
  name = '',
  error = '',
  helpText = '',
  renderEmpty,
  emptyOptions = '',
  filterCallback,
  isDataLoading = false,
  loadingIcon = '',
  additionalClasses = '',
  additionalOptionsClasses = '',
  additionalErrorClasses = '',
  testId = '',
  additionalDropdownClasses = '',
  anchor,
  additionalContainerClasses = '',
  filterCallbackOnEmpty = false,
  debounceTime = 0,
  ignoreNotFound = false,
  renderOnEmptyMatch,
  extraOptions = EMPTY_ARRAY,
  maxVisibleValues,
  maxVisibleValuesText,
  placeholderMultiple = '',
  isVirtualScrolling = false,
  editors,
  variant = 'default',
  additionalInputClasses = '',
  additionalHelpTextClasses = '',
}: DropdownProps) => {
  const [currentValue, setCurrentValue] = useState<any>(value);
  const [query, setQuery] = useState('');
  const [customFilteredOptions, setCustomFilteredOptions] = useState<
    DropdownOption[]
  >([]);
  const [selectedForDelete, setSelectedForDelete] = useState(null);

  const [isLoading, setIsLoading] = useState(isDataLoading);
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const changesDisabled = disabled || readonly;

  const isFilterDropdown = ['operator', 'filter'].includes(variant);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  useEffect(() => {
    setIsLoading(isDataLoading);
  }, [isDataLoading]);

  const currentOptions = useMemo(() => {
    const newOptions = [...options];
    let val = currentValue;
    if (!Array.isArray(val)) val = [currentValue];
    val.forEach((element: DropdownOption) => {
      const optionsVal = newOptions.find((el) => el.value === element);
      const customFiltersVal = customFilteredOptions.find(
        (el) => el.value === element,
      );
      const extraOptionsVal = extraOptions.find((el) => el.value === element);
      if (!optionsVal && customFiltersVal) {
        newOptions.push(customFiltersVal);
      }
      if (!optionsVal && !customFiltersVal && extraOptionsVal) {
        newOptions.push(extraOptionsVal);
      }
    });
    return newOptions;
  }, [options, currentValue, customFilteredOptions, extraOptions]);

  const selectedOptions = useMemo(() => {
    let newVal = currentValue;
    if (!multiple) {
      if (Array.isArray(currentValue)) {
        newVal = currentValue[0];
        setCurrentValue(newVal);
      }
      const optionFound = currentOptions.find(
        (option) => option.value === newVal,
      );
      if (optionFound) return optionFound;
      return newVal
        ? {
            value: newVal,
            label: newVal,
            notFound: true,
          }
        : null;
    }
    if (!Array.isArray(currentValue)) {
      newVal = currentValue ? [currentValue] : [];
      setCurrentValue(newVal);
    }
    return newVal
      .map(
        (val: any) =>
          currentOptions.find((option) => option.value === val) || {
            value: val,
            label: val,
            notFound: true,
          },
      )
      .filter((option: DropdownOption) => !!option);
  }, [currentValue, multiple, currentOptions]);

  const handleInputBlur = useCallback(() => {
    setSelectedForDelete(null);
  }, []);

  const searchChangeCallback = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value);
    },
    [],
  );

  const handleSearchChange = useDebounceCallback(
    searchChangeCallback,
    debounceTime,
  );

  const handleChange = useCallback(
    (val: any) => {
      if (!multiple && !nullable && val === null) return;

      if (!multiple) {
        setTimeout(() => {
          inputRef.current?.blur();
        });
      }
      setCurrentValue(val);
      if (onChange) {
        const newValue = onChange(
          {
            target: { value: val, name },
            option:
              customFilteredOptions[
                customFilteredOptions.findIndex((el) => el.value === val)
              ] || options[options.findIndex((el) => el.value === val)],
          },
          val,
        );

        if (typeof newValue !== 'undefined') {
          setCurrentValue(newValue);
        }
      }

      if (multiple) {
        if (inputRef.current) inputRef.current.value = '';
        setQuery('');
      }

      onBlur?.();
    },
    [
      multiple,
      nullable,
      onChange,
      onBlur,
      name,
      customFilteredOptions,
      options,
    ],
  );

  const deleteSelected = useCallback<KeyboardEventHandler<HTMLInputElement>>(
    (event) => {
      /**
       * We need to add stop propagation due to the issue:
       * https://github.com/tailwindlabs/headlessui/discussions/1687
       */
      event.stopPropagation();

      if (!multiple) return;
      if (event.key === 'Backspace') {
        if (query === '') {
          if (selectedForDelete === null) {
            setSelectedForDelete(currentValue[currentValue.length - 1]);
            return;
          }

          const newValue = currentValue.filter(
            (val: any) => val !== selectedForDelete,
          );

          handleChange(newValue);
          setSelectedForDelete(null);
        }
      }
    },
    [currentValue, handleChange, multiple, query, selectedForDelete],
  );

  const handleDeleteSelected = useDebounceCallback(deleteSelected);

  const fetchFilterData = useCallback(
    async (
      query: string,
      options: DropdownOption[],
      setIsLoading: Dispatch<SetStateAction<boolean>>,
      currentValue: string,
    ) => {
      if (!filterCallback || !query) return;
      const filtered = await filterCallback(
        query,
        options,
        setIsLoading,
        currentValue,
      );
      setCustomFilteredOptions(filtered || []);
    },
    [filterCallback],
  );

  useEffect(() => {
    fetchFilterData(query, options, setIsLoading, currentValue);
  }, [fetchFilterData, options, query, filterCallback, currentValue]);

  const filteredOptions = useMemo(() => {
    const strictFilter =
      query === ''
        ? currentOptions
        : [...currentOptions, ...customFilteredOptions].filter((option) => {
            return option.label === query;
          });

    if (filterCallback && query)
      return { filterResult: customFilteredOptions, strictFilter };

    const filterResult =
      query === ''
        ? currentOptions
        : currentOptions.filter((option) => {
            let optionSearch = '';
            if (typeof option.label === 'string') {
              optionSearch = option.label;
            }
            if (option.searchString) {
              optionSearch = option.searchString;
            }
            return filterGroups
              ? checkIfIncludes(optionSearch, query) ||
                  checkIfIncludes(option.group || '', query)
              : checkIfIncludes(optionSearch, query);
          });

    return { filterResult, strictFilter };
  }, [
    currentOptions,
    query,
    filterGroups,
    filterCallback,
    customFilteredOptions,
  ]);

  const groupedOptions = useMemo(
    () =>
      filteredOptions?.filterResult?.reduce(
        (acc, option) => {
          const groupName = option.group || '';
          if (!acc[groupName]) {
            acc[groupName] = [];
          }
          acc[groupName].push(option);
          return acc;
        },
        {} as Record<string, DropdownOption[]>,
      ),
    [filteredOptions],
  );

  const handleRemoveItem = useCallback<DropdownRemoveOptionFn>(
    (removed) => {
      const newValue = currentValue.filter((val: any) => val !== removed.value);
      handleChange(newValue);
    },
    [handleChange, currentValue],
  );

  const handleClear = useCallback<MouseEventHandler<HTMLOrSVGElement>>(
    (e) => {
      e.stopPropagation();
      const newValue = multiple ? [] : null;
      handleChange(newValue);
    },
    [handleChange, multiple],
  );

  const renderWithoutOptions = useMemo(() => {
    if (isLoading)
      return loadingIcon ? (
        loadingIcon
      ) : (
        <div
          className="flex items-center justify-center w-full h-full p-4"
          {...getTestProps(testId, 'loading')}
        >
          <Loader size="small" type="spinner-grid" />
        </div>
      );

    if (filteredOptions?.filterResult?.length === 0) {
      if (renderEmpty && query !== '') return renderEmpty?.(query);
      if (emptyOptions && query === '') return emptyOptions;
    }
    return null;
  }, [
    isLoading,
    filteredOptions,
    query,
    renderEmpty,
    emptyOptions,
    loadingIcon,
    testId,
  ]);

  const handleAddOnEmpty = useMemo(() => {
    if (filteredOptions?.strictFilter?.length === 0) {
      return renderOnEmptyMatch?.(query);
    }
    return null;
  }, [filteredOptions, query, renderOnEmptyMatch]);

  const getInputValue = useCallback(() => {
    if (!multiple)
      return typeof selectedOptions?.label !== 'string'
        ? selectedOptions?.searchString
        : selectedOptions?.label || selectedOptions?.value;
    return '';
  }, [
    multiple,
    selectedOptions?.label,
    selectedOptions?.searchString,
    selectedOptions?.value,
  ]);

  const IconComponent =
    variant === 'operator' ? ArrowFilledDownIcon : CaretDownIcon;

  const renderLabelAndInput = useMemo(
    () => (
      <div
        className={twMerge(
          'min-w-6 max-w-full flex gap-y-1 gap-x-1 dark:text-slate-200',
          (!maxVisibleValues || maxVisibleValues > 1) && 'flex-wrap',
          isFilterDropdown && 'leading-[100%]',
        )}
        {...getTestProps(testId, 'selected-options')}
      >
        {((multiple && selectedOptions.length) ||
          hideSearch ||
          changesDisabled) && (
          <SelectedOptions
            placeholder={placeholder}
            multiple={multiple}
            selectedOptions={selectedOptions}
            onRemove={handleRemoveItem}
            disabled={changesDisabled}
            testId={testId}
            isSearch={!hideSearch}
            ignoreNotFound={ignoreNotFound}
            maxVisibleValues={maxVisibleValues}
            maxVisibleValuesText={maxVisibleValuesText}
            selectedForDelete={selectedForDelete}
          />
        )}
        {!changesDisabled && (
          <>
            {!hideSearch && (
              <span
                className={twMerge(
                  'h-2 w-2 rounded-full absolute',
                  selectedOptions?.circleColor || 'hidden',
                )}
                {...getTestProps(testId, 'circle-color')}
              />
            )}
            <ComboboxInput
              ref={inputRef}
              className={twMerge(
                'text-ellipsis text-base grow min-w-3 w-fit max-w-full focus:outline-hidden',
                'placeholder:text-slate-400 placeholder:font-light dark:bg-transparent dark:text-slate-200',
                !selectedOptions?.length && 'w-full',
                selectedOptions?.circleColor && 'pl-4',
                !multiple &&
                  selectedOptions?.notFound &&
                  query === '' &&
                  !ignoreNotFound &&
                  'text-red',
                additionalInputClasses,
                hideSearch &&
                  'pointer-events-none absolute inset-0 h-full w-full opacity-0',
              )}
              displayValue={getInputValue}
              onChange={handleSearchChange}
              onKeyDown={handleDeleteSelected}
              onBlur={handleInputBlur}
              autoComplete="off"
              placeholder={
                !multiple || !selectedOptions.length
                  ? placeholder
                  : placeholderMultiple
              }
              onFocus={onFocus}
              onBlurCapture={onBlur}
              readOnly={hideSearch}
              tabIndex={hideSearch ? -1 : undefined}
              id={name}
              aria-label={placeholder || name}
              {...getTestProps(testId, hideSearch ? 'anchor-input' : 'input')}
            />
          </>
        )}
      </div>
    ),
    [
      maxVisibleValues,
      isFilterDropdown,
      testId,
      multiple,
      selectedOptions,
      hideSearch,
      changesDisabled,
      placeholder,
      handleRemoveItem,
      ignoreNotFound,
      maxVisibleValuesText,
      selectedForDelete,
      query,
      additionalInputClasses,
      getInputValue,
      handleSearchChange,
      handleDeleteSelected,
      handleInputBlur,
      placeholderMultiple,
      name,
      onFocus,
      onBlur,
    ],
  );

  const handleAfterEnter = () => {
    if (filterCallbackOnEmpty && !query) {
      setQuery(`_initOnEmpty`);
    }
  };

  return (
    <Combobox
      value={currentValue}
      onChange={handleChange}
      multiple={multiple}
      disabled={changesDisabled}
      name={name}
      immediate
      {...(isVirtualScrolling
        ? { virtual: { options: filteredOptions?.filterResult || [] } }
        : {})}
    >
      {({ open }) => (
        <div className={twMerge(additionalClasses)}>
          <div className="relative w-full flex flex-col">
            {label && (
              <label
                htmlFor={name}
                className={twMerge(
                  'text-sm text-slate-400 dark:text-slate-200 mb-1',
                )}
                {...getTestProps(testId, 'label')}
              >
                {label}
                {required && <RequiredTemplate testId={testId} />}
              </label>
            )}

            <FieldEditorsIndicator editors={editors}>
              <ComboboxButton
                className={twMerge(
                  'grid grid-cols-[1fr_fit-content(100px)] gap-0.5 md:gap-2',
                  'w-full items-center justify-between max-h-full cursor-pointer',
                  'cursor-default bg-white dark:bg-transparent focus-within:border-blue px-4 py-2.5',
                  'overflow-hidden rounded-lg text-left border border-slate-200 dark:border-slate-700',
                  disabled &&
                    'bg-slate-100 cursor-not-allowed dark:bg-slate-900',
                  readonly && 'cursor-not-allowed',
                  error != null && !!error && 'border-red',
                  multiple && !isFilterDropdown ? 'min-h-[48px]' : 'h-12',
                  isFilterDropdown && 'h-5.5 py-0 px-1.5 border-0 rounded-sm',
                  variant === 'operator' && 'bg-slate-100 dark:bg-slate-600',
                  additionalContainerClasses,
                )}
                ref={buttonRef}
                as="div"
                {...getTestProps(testId, 'container')}
              >
                {renderLabelAndInput}
                <div className="inline-flex items-center w-full text-slate-600 dark:text-slate-200">
                  {((multiple && selectedOptions.length > 0) ||
                    (!multiple && selectedOptions && nullable)) &&
                    !changesDisabled && (
                      <CloseIcon
                        className={twMerge(
                          'cursor-pointer w-3 mr-2 hover:opacity-30',
                          isFilterDropdown && 'h-2 w-2',
                        )}
                        onClick={handleClear}
                        {...getTestProps(testId, 'clear-all')}
                      />
                    )}
                  <IconComponent
                    className={twMerge(
                      'w-3.5',
                      open ? 'rotate-180 transform' : '',
                      changesDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
                      isFilterDropdown && 'h-1.5 w-2.5',
                    )}
                    {...getTestProps(testId, 'arrow-icon')}
                  />
                </div>
              </ComboboxButton>
            </FieldEditorsIndicator>

            <Transition
              afterLeave={() => setQuery('')}
              afterEnter={handleAfterEnter}
            >
              <DropdownOptions
                handleAddOnEmpty={handleAddOnEmpty}
                groupedOptions={groupedOptions}
                renderWithoutOptions={renderWithoutOptions}
                isVirtualScrolling={isVirtualScrolling}
                isFilterDropdown={isFilterDropdown}
                additionalDropdownClasses={additionalDropdownClasses}
                anchor={anchor ?? (multiple ? false : undefined)}
                additionalOptionsClasses={additionalOptionsClasses}
                testId={testId}
              />
            </Transition>
          </div>
          <HelpErrorTextsTemplate
            helpText={helpText}
            error={error}
            additionalErrorClasses={additionalErrorClasses}
            additionalHelpClasses={additionalHelpTextClasses}
            testId={testId}
          />
        </div>
      )}
    </Combobox>
  );
};

export default Dropdown;
